import { Box, Button, Heading, MessageModalLayout, Modal, ModalMobileLayout, Text } from '@wix/design-system';
import { classes as editor3Classes } from '@wix/design-system-tokens/odeditor/all.st.css';
import { classes as studioClasses } from '@wix/design-system-tokens/studio/all.st.css';
import { useTranslation } from '@wix/wix-i18n-config';
import classNames from 'classnames';
import React, { FC, useCallback } from 'react';

import { TLocalesKeys } from '../../../../types';
import { experiments } from '../../../experiments';
import { renderModel } from '../../../util/renderModel';
import { useCloseLiveChat } from './useCloseLiveChat';

export type EndLiveChatModalProps = {
  isOpen: boolean;
  onCloseModal: () => void;
  isMobileView?: boolean;
};

const isMobile = renderModel.isMobile();
const isCopilot = renderModel.isCopilot();
const isEditor3 = renderModel.isEditor3();

const isStudioEditor = renderModel.isStudioEditor();
const isStudioRedesign = isStudioEditor && experiments.is('specs.helpwidget.newHomePageStudioDesign', 'true');
export const EndLiveChatModal: FC<EndLiveChatModalProps> = ({
  isOpen,
  onCloseModal,
  isMobileView = isMobile,
}) => {
  const { t } = useTranslation();
  const { isDisabled, closeChat } = useCloseLiveChat();

  const onEndChat = useCallback(async () => {
    await closeChat();
    onCloseModal();
  }, [closeChat, onCloseModal]);

  const titleText = t<TLocalesKeys>('end_chat_widget');
  const contentText = t<TLocalesKeys>('end_chat_modal_content');
  const primaryButtonText = t<TLocalesKeys>('widget_end_live_chat_yes');
  const secondaryButtonText = t<TLocalesKeys>('widget_end_live_chat_no');
  const modalMobileLayoutContent = isStudioRedesign ? (
    <Box minWidth="264px">
      <Text size="small" weight="thin">
        {contentText}
      </Text>
    </Box>
  ) : (
    <Text size="small" weight="thin">
      {contentText}
    </Text>
  );
  return (
    <Modal isOpen={isOpen} screen={isMobileView ? 'mobile' : 'desktop'}>
      {isMobileView ? (
        <ModalMobileLayout
          onCloseButtonClick={onCloseModal}
          title={
            isCopilot ? <Text weight="bold">{titleText}</Text> : <Heading size="medium">{titleText}</Heading>
          }
          content={modalMobileLayoutContent}
          footer={
            <Box align="right" gap={2}>
              <Button size={isCopilot ? 'tiny' : 'small'} priority="secondary" onClick={onCloseModal}>
                {secondaryButtonText}
              </Button>
              <Button size={isCopilot ? 'tiny' : 'small'} onClick={onEndChat} disabled={isDisabled}>
                {primaryButtonText}
              </Button>
            </Box>
          }
          className={classNames({
            [studioClasses.root]: isStudioRedesign,
            [editor3Classes.root]: isEditor3,
          })}
        />
      ) : (
        <MessageModalLayout
          onCloseButtonClick={onCloseModal}
          primaryButtonOnClick={onEndChat}
          primaryButtonProps={{ disabled: isDisabled }}
          secondaryButtonOnClick={onCloseModal}
          primaryButtonText={primaryButtonText}
          secondaryButtonText={secondaryButtonText}
          title={titleText}
          content={<Text weight="thin">{contentText}</Text>}
        />
      )}
    </Modal>
  );
};
